import React from "react";
import { Info, Plus, Trash2 } from "lucide-react";
import { formatINR, parseNum } from "./format";

export const SectionCard = ({ title, subtitle, children, right, testId }) => (
    <div className="rounded-3xl border border-oth-line/70 bg-white p-6 md:p-8" data-testid={testId}>
        <div className="flex items-start justify-between gap-4 mb-5">
            <div>
                <h3 className="font-display text-lg font-semibold tracking-tight">{title}</h3>
                {subtitle && <p className="text-sm text-oth-ink3 font-body mt-1">{subtitle}</p>}
            </div>
            {right}
        </div>
        {children}
    </div>
);

export const FieldLabel = ({ children, tip }) => (
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1.5">
        {children}
        {tip && (
            <span className="relative group inline-flex">
                <Info size={12} className="text-oth-ink3 cursor-help" />
                <span className="pointer-events-none absolute left-0 top-5 z-30 hidden group-hover:block w-64 normal-case tracking-normal text-[12px] leading-snug bg-[#0A0A0A] text-white rounded-xl px-3 py-2 shadow-lg">
                    {tip}
                </span>
            </span>
        )}
    </div>
);

const inputCls = "w-full bg-white border border-oth-line rounded-xl px-3.5 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue transition-colors";

export const MoneyInput = ({ label, value, onChange, tip, testId, placeholder = "0" }) => (
    <label className="block">
        {label && <FieldLabel tip={tip}>{label}</FieldLabel>}
        <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-oth-ink3 font-body">₹</span>
            <input
                inputMode="numeric"
                className={`${inputCls} pl-8`}
                value={value === "" || value === null || value === undefined ? "" : formatINR(value)}
                onChange={(e) => onChange(parseNum(e.target.value))}
                placeholder={placeholder}
                data-testid={testId}
            />
        </div>
    </label>
);

export const PctInput = ({ label, value, onChange, tip, testId, placeholder = "0", min = -20, max = 60 }) => (
    <label className="block">
        {label && <FieldLabel tip={tip}>{label}</FieldLabel>}
        <div className="relative">
            <input
                type="number" step="0.1" min={min} max={max}
                className={`${inputCls} pr-8`}
                value={value === "" || value === null || value === undefined ? "" : value}
                onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
                placeholder={placeholder}
                data-testid={testId}
            />
            <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-sm text-oth-ink3 font-body">%</span>
        </div>
    </label>
);

export const YearSelect = ({ label, value, onChange, tip, testId, startYear, span = 60, allowEmpty, emptyLabel = "—" }) => {
    const start = startYear || new Date().getFullYear();
    const years = Array.from({ length: span }, (_, i) => start + i);
    return (
        <label className="block">
            {label && <FieldLabel tip={tip}>{label}</FieldLabel>}
            <select
                className={inputCls}
                value={value || ""}
                onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
                data-testid={testId}
            >
                {allowEmpty && <option value="">{emptyLabel}</option>}
                {!allowEmpty && !value && <option value="">Select year</option>}
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
        </label>
    );
};

export const NumInput = ({ label, value, onChange, tip, testId, min = 0, max = 60, placeholder = "" }) => (
    <label className="block">
        {label && <FieldLabel tip={tip}>{label}</FieldLabel>}
        <input
            type="number" min={min} max={max}
            className={inputCls}
            value={value === "" || value === null || value === undefined ? "" : value}
            onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
            placeholder={placeholder}
            data-testid={testId}
        />
    </label>
);

export const TextInput = ({ label, value, onChange, tip, testId, placeholder = "" }) => (
    <label className="block">
        {label && <FieldLabel tip={tip}>{label}</FieldLabel>}
        <input
            className={inputCls}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            data-testid={testId}
        />
    </label>
);

export const ToggleRow = ({ label, sub, on, onToggle, testId }) => (
    <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between rounded-2xl border px-5 py-4 text-left transition-colors ${on ? "border-oth-blue bg-[#eef3f8]" : "border-oth-line/70 bg-white hover:border-oth-ink3"}`}
        data-testid={testId}
    >
        <div>
            <div className="font-body text-sm font-medium text-oth-ink">{label}</div>
            {sub && <div className="text-xs text-oth-ink3 font-body mt-0.5">{sub}</div>}
        </div>
        <span className={`relative w-10 h-6 rounded-full transition-colors shrink-0 ${on ? "bg-oth-blue" : "bg-oth-line"}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
        </span>
    </button>
);

export const RepeatList = ({ items, onChange, render, blank, addLabel, testId }) => (
    <div className="space-y-3">
        {items.map((it, i) => (
            <div key={it._k || i} className="rounded-2xl border border-oth-line/70 bg-[#fafaf8] p-4 relative">
                <button
                    type="button"
                    onClick={() => onChange(items.filter((_, idx) => idx !== i))}
                    className="absolute top-3 right-3 w-7 h-7 rounded-full border border-oth-line/70 flex items-center justify-center text-oth-ink3 hover:text-red-600 hover:border-red-300 transition-colors"
                    data-testid={`${testId}-remove-${i}`}
                    aria-label="Remove"
                ><Trash2 size={13} /></button>
                {render(it, (next) => onChange(items.map((x, idx) => (idx === i ? next : x))), i)}
            </div>
        ))}
        <button
            type="button"
            onClick={() => onChange([...items, { ...blank(), _k: Math.random().toString(36).slice(2) }])}
            className="inline-flex items-center gap-1.5 text-sm font-body text-oth-blue hover:text-oth-ink transition-colors"
            data-testid={`${testId}-add`}
        ><Plus size={14} /> {addLabel}</button>
    </div>
);
